import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Copy, Check, RotateCw, ChevronDown, ChevronRight, Globe, Megaphone, Mail, UserCog, ExternalLink, Send } from 'lucide-react';
import { SOURCES, PRIORITIES } from '@/lib/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationsTab() {
    const { profile } = useAuth();
    const [configs, setConfigs] = useState([]);
    const [agents, setAgents] = useState([]);
    const [openSection, setOpenSection] = useState('website');

    const load = useCallback(async () => {
        const [{ data: cfgs }, { data: ag }] = await Promise.all([
            supabase.from('lead_sources_config').select('*').order('source_type'),
            supabase.from('profiles').select('id, full_name, email, role'),
        ]);
        setConfigs(cfgs || []);
        setAgents(ag || []);
    }, []);
    useEffect(() => { load(); }, [load]);

    const get = (type) => configs.find((c) => c.source_type === type);

    return (
        <div className="space-y-3" data-testid="integrations-tab">
            <Section open={openSection === 'website'} onToggle={() => setOpenSection(openSection === 'website' ? null : 'website')} icon={<Globe className="h-5 w-5" />} title="Website integration" badge={get('website')?.website_api_key ? 'Active' : 'Not configured'} testId="section-website">
                <WebsiteSection cfg={get('website')} onChange={load} />
            </Section>
            <Section open={openSection === 'meta'} onToggle={() => setOpenSection(openSection === 'meta' ? null : 'meta')} icon={<Megaphone className="h-5 w-5" />} title="Meta Lead Ads (Instagram + Facebook)" badge={get('meta')?.meta_app_id ? 'Active' : 'Not configured'} testId="section-meta">
                <MetaSection cfg={get('meta')} onChange={load} />
            </Section>
            <Section open={openSection === 'email'} onToggle={() => setOpenSection(openSection === 'email' ? null : 'email')} icon={<Mail className="h-5 w-5" />} title="Bayut & PropertyFinder (Email parser)" badge="Manual import ready" testId="section-email">
                <EmailSection orgId={profile?.organization_id} />
            </Section>
            <Section open={openSection === 'defaults'} onToggle={() => setOpenSection(openSection === 'defaults' ? null : 'defaults')} icon={<UserCog className="h-5 w-5" />} title="Default lead assignment" badge={`${configs.length} sources`} testId="section-defaults">
                <DefaultsSection configs={configs} agents={agents} onChange={load} />
            </Section>
        </div>
    );
}

function Section({ open, onToggle, title, badge, icon, children, testId }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={testId}>
            <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 hover:bg-gray-50">
                <div className="h-9 w-9 rounded-md bg-aks-gold-soft text-aks-navy flex items-center justify-center">{icon}</div>
                <div className="flex-1 text-left">
                    <div className="font-semibold text-[14px] text-aks-text">{title}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold">{badge}</span>
                {open ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
            </button>
            {open && <div className="border-t border-gray-100 p-5 space-y-4">{children}</div>}
        </div>
    );
}

function CopyButton({ value, label = 'Copy' }) {
    const [c, setC] = useState(false);
    return (
        <button onClick={async () => { await navigator.clipboard.writeText(value); setC(true); setTimeout(() => setC(false), 1200); }} className="h-9 px-3 rounded-md border border-gray-300 hover:border-aks-gold text-[12px] inline-flex items-center gap-1.5">
            {c ? <><Check className="h-4 w-4 text-aks-success" /> Copied</> : <><Copy className="h-4 w-4" /> {label}</>}
        </button>
    );
}

function WebsiteSection({ cfg, onChange }) {
    const [testing, setTesting] = useState(false);
    const apiKey = cfg?.website_api_key || '';
    const url = `${BACKEND_URL}/api/leads/website`;

    const regenerate = async () => {
        if (!confirm('Regenerate the API key? Existing forms will stop working until you update them.')) return;
        const r = await api.regenerateKey('website');
        toast.success('New API key generated');
        onChange();
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            await api.testWebsiteLead(apiKey, {
                full_name: 'Test Lead (' + new Date().toLocaleTimeString() + ')',
                phone: '+971500000000',
                email: 'test@example.com',
                budget_aed: 1000000,
                property_type: 'apartment',
                emirate_preference: 'Dubai',
                message: 'Sent from Settings → Test button',
            });
            toast.success('Test lead sent! Check the dashboard live feed.');
        } catch (err) { toast.error(err.message); } finally { setTesting(false); }
    };

    const snippet = `<!-- Paste this on your site -->
<form id="aksaaf-form">
  <input name="full_name" placeholder="Full name" required />
  <input name="phone" placeholder="Phone" required />
  <input name="email" placeholder="Email" />
  <textarea name="message" placeholder="Message"></textarea>
  <button type="submit">Send</button>
</form>
<script>
  document.getElementById('aksaaf-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd.entries());
    data.page_url = location.href;
    const res = await fetch('${url}', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': '${apiKey}'
      },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    if (json.success) alert('Thanks! We will contact you shortly.');
    else alert('Sorry, please try again.');
  });
</script>`;

    return (
        <div className="space-y-3">
            <p className="text-[13px] text-gray-600">Paste the snippet on your website to start receiving leads directly into your pipeline.</p>
            <Field label="Endpoint URL"><div className="flex gap-2"><code className="flex-1 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-[12px] truncate">{url}</code><CopyButton value={url} /></div></Field>
            <Field label="Your API key">
                <div className="flex gap-2">
                    <code className="flex-1 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-[12px] font-mono truncate">{apiKey || '(none)'}</code>
                    <CopyButton value={apiKey} />
                    <button onClick={regenerate} className="h-9 px-3 rounded-md border border-gray-300 hover:border-aks-danger text-[12px] inline-flex items-center gap-1.5" data-testid="regen-website-key"><RotateCw className="h-4 w-4" /> Regenerate</button>
                </div>
            </Field>
            <Field label="Code snippet (HTML + JS)">
                <div className="relative">
                    <pre className="max-h-[280px] overflow-auto p-3 rounded-md bg-aks-navy text-white text-[11px] font-mono">{snippet}</pre>
                    <div className="absolute top-2 right-2"><CopyButton value={snippet} label="Copy snippet" /></div>
                </div>
            </Field>
            <button onClick={sendTest} disabled={testing || !apiKey} className="h-10 px-4 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px] inline-flex items-center gap-2 disabled:opacity-60" data-testid="test-website-btn">
                <Send className="h-4 w-4" /> {testing ? 'Sending…' : 'Send a test lead'}
            </button>
        </div>
    );
}

function MetaSection({ cfg, onChange }) {
    const [appId, setAppId] = useState(cfg?.meta_app_id || '');
    const [appSecret, setAppSecret] = useState(cfg?.meta_app_secret || '');
    const [pageId, setPageId] = useState(cfg?.meta_page_id || '');
    const [saving, setSaving] = useState(false);
    const url = `${BACKEND_URL}/api/leads/meta`;
    const verifyToken = cfg?.meta_webhook_verify_token || '';

    useEffect(() => { setAppId(cfg?.meta_app_id || ''); setAppSecret(cfg?.meta_app_secret || ''); setPageId(cfg?.meta_page_id || ''); }, [cfg?.id]);

    const save = async () => {
        if (!cfg) return;
        setSaving(true);
        try {
            const { error } = await supabase.from('lead_sources_config').update({ meta_app_id: appId, meta_app_secret: appSecret, meta_page_id: pageId, is_enabled: !!appId }).eq('id', cfg.id);
            if (error) throw error;
            toast.success('Meta config saved');
            onChange();
        } catch (err) { toast.error(err.message); } finally { setSaving(false); }
    };
    const regen = async () => {
        if (!confirm('Regenerate verify token? You will need to update it in Meta dashboard.')) return;
        await api.regenerateKey('meta');
        toast.success('New verify token generated');
        onChange();
    };

    return (
        <div className="space-y-3">
            <p className="text-[13px] text-gray-600">Configure the Meta Lead Ads webhook so submissions from Instagram + Facebook ads land directly in your pipeline.</p>
            <Field label="Callback URL (use this in Meta)"><div className="flex gap-2"><code className="flex-1 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-[12px] truncate">{url}</code><CopyButton value={url} /></div></Field>
            <Field label="Webhook verify token">
                <div className="flex gap-2">
                    <code className="flex-1 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-[12px] font-mono truncate">{verifyToken}</code>
                    <CopyButton value={verifyToken} />
                    <button onClick={regen} className="h-9 px-3 rounded-md border border-gray-300 hover:border-aks-danger text-[12px] inline-flex items-center gap-1.5"><RotateCw className="h-4 w-4" /> Regenerate</button>
                </div>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="App ID"><input value={appId} onChange={(e) => setAppId(e.target.value)} className="w-full h-10 px-3 rounded-md border border-gray-300 text-[13px]" /></Field>
                <Field label="App Secret"><input type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} className="w-full h-10 px-3 rounded-md border border-gray-300 text-[13px]" /></Field>
                <Field label="Page ID"><input value={pageId} onChange={(e) => setPageId(e.target.value)} className="w-full h-10 px-3 rounded-md border border-gray-300 text-[13px]" /></Field>
            </div>
            <button onClick={save} disabled={saving} className="h-10 px-4 rounded-md bg-aks-navy text-white font-semibold text-[13px] disabled:opacity-60">{saving ? 'Saving…' : 'Save Meta config'}</button>
            <details className="mt-2 bg-aks-gold-soft border border-[rgba(201,168,76,0.32)] rounded-md p-4">
                <summary className="font-semibold text-[13px] cursor-pointer">Setup instructions</summary>
                <ol className="mt-3 ml-5 list-decimal text-[13px] text-aks-text space-y-1">
                    <li>Go to <a className="text-aks-navy underline" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">Meta for Developers <ExternalLink className="inline h-3 w-3" /></a> → Your App → Webhooks</li>
                    <li>Subscribe to the <b>leadgen</b> event on your Facebook Page</li>
                    <li>Set Callback URL to the URL shown above</li>
                    <li>Set Verify Token to the value shown above</li>
                    <li>Enter App ID, App Secret, and Page ID here and Save</li>
                    <li>Test with Meta's <a className="text-aks-navy underline" href="https://developers.facebook.com/tools/lead-ads-testing" target="_blank" rel="noreferrer">Lead Ads Testing Tool</a></li>
                </ol>
            </details>
        </div>
    );
}

function EmailSection({ orgId }) {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [busy, setBusy] = useState(false);
    const [parsed, setParsed] = useState(null);
    const inboxAddress = `leads-${(orgId || '').slice(0, 8)}@aksaaf-crm.com`;
    const postmarkUrl = `${BACKEND_URL}/api/leads/email-parser`;

    const parse = async () => {
        setBusy(true);
        try {
            const r = await api.parseEmail(subject, body, orgId);
            setParsed(r.parsed);
            toast.success('Lead created from parsed email');
        } catch (err) { toast.error(err.message); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4">
            <p className="text-[13px] text-gray-600">Receive leads from Bayut and PropertyFinder via email. Two options:</p>

            <div className="bg-aks-gold-soft border border-[rgba(201,168,76,0.32)] rounded-md p-4">
                <div className="font-semibold text-[13px] mb-2">Option A — Manual paste (works now)</div>
                <p className="text-[12px] text-gray-700">When you receive a lead email from Bayut or PropertyFinder, paste it below. The CRM auto-detects the source and extracts name, phone, email, message.</p>
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" className="h-10 px-3 rounded-md border border-gray-300 text-[13px]" data-testid="email-subject" />
                    <textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Paste full email body here…" className="col-span-1 lg:col-span-2 p-3 rounded-md border border-gray-300 text-[13px]" data-testid="email-body" />
                </div>
                <button onClick={parse} disabled={busy || !body} className="mt-3 h-10 px-4 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px] disabled:opacity-60" data-testid="parse-email-btn">
                    {busy ? 'Parsing…' : 'Parse & Import'}
                </button>
                {parsed && (
                    <div className="mt-3 p-3 bg-white rounded border border-gray-200 text-[12px]">
                        <div><b>Source:</b> {parsed.source}</div>
                        <div><b>Name:</b> {parsed.full_name}</div>
                        <div><b>Phone:</b> {parsed.phone}</div>
                        <div><b>Email:</b> {parsed.email}</div>
                        {parsed.notes && <div className="mt-1"><b>Notes:</b> {parsed.notes}</div>}
                    </div>
                )}
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-4">
                <div className="font-semibold text-[13px] mb-2">Option B — Postmark Inbound (auto, ready to activate)</div>
                <p className="text-[12px] text-gray-700">Forward your portal lead emails to a Postmark inbound address. The CRM endpoint is ready at:</p>
                <div className="mt-2 flex gap-2">
                    <code className="flex-1 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-[12px] truncate">{postmarkUrl}</code>
                    <CopyButton value={postmarkUrl} />
                </div>
                <ol className="mt-3 ml-5 list-decimal text-[12px] text-gray-700 space-y-1">
                    <li>Sign up at <a href="https://postmarkapp.com" target="_blank" rel="noreferrer" className="text-aks-navy underline">postmarkapp.com</a> → create an Inbound Stream</li>
                    <li>Set Webhook URL to <code>{postmarkUrl}</code></li>
                    <li>You’ll receive an inbound email address (e.g. <code>{inboxAddress}</code>) — forward your Bayut/PF leads to it</li>
                    <li><b>Bayut:</b> Account Settings → Lead notifications email</li>
                    <li><b>PropertyFinder:</b> Agent dashboard → Lead forwarding email</li>
                </ol>
            </div>
        </div>
    );
}

function DefaultsSection({ configs, agents, onChange }) {
    return (
        <div className="space-y-2">
            <p className="text-[13px] text-gray-600 mb-2">Set default agent + priority + admin notifications for each lead source.</p>
            <table className="w-full text-[13px]">
                <thead className="text-[10px] uppercase tracking-wider text-gray-500"><tr><th className="text-left py-2">Source</th><th className="text-left">Default agent</th><th className="text-left">Default priority</th><th className="text-left">Notify admin</th><th className="text-left">Enabled</th></tr></thead>
                <tbody>
                    {configs.map((c) => <DefaultsRow key={c.id} cfg={c} agents={agents} onChange={onChange} />)}
                </tbody>
            </table>
        </div>
    );
}

function DefaultsRow({ cfg, agents, onChange }) {
    const [busy, setBusy] = useState(false);
    const update = async (patch) => {
        setBusy(true);
        const { error } = await supabase.from('lead_sources_config').update(patch).eq('id', cfg.id);
        if (error) toast.error(error.message); else onChange();
        setBusy(false);
    };
    return (
        <tr className="border-t border-gray-100">
            <td className="py-2 capitalize font-semibold">{cfg.source_type}</td>
            <td className="py-2">
                <select value={cfg.default_assigned_agent_id || ''} onChange={(e) => update({ default_assigned_agent_id: e.target.value || null })} className="h-8 px-2 rounded border border-gray-300 text-[12px]">
                    <option value="">Unassigned</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
                </select>
            </td>
            <td className="py-2">
                <select value={cfg.default_priority || 'Warm'} onChange={(e) => update({ default_priority: e.target.value })} className="h-8 px-2 rounded border border-gray-300 text-[12px]">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
            </td>
            <td className="py-2">
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!cfg.auto_notify_admin} onChange={(e) => update({ auto_notify_admin: e.target.checked })} /> <span className="text-[12px]">Notify</span></label>
            </td>
            <td className="py-2">
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!cfg.is_enabled} onChange={(e) => update({ is_enabled: e.target.checked })} /> <span className="text-[12px]">{cfg.is_enabled ? 'On' : 'Off'}</span></label>
            </td>
        </tr>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</div>
            {children}
        </div>
    );
}
